import { Archive, CheckCircle, Folder, FolderOpen } from "@phosphor-icons/react"
import { createFileRoute, Outlet } from "@tanstack/react-router"
import { zodValidator } from "@tanstack/zod-adapter"
import { useMemo } from "react"
import { z } from "zod"
import { useSessionStorage } from "usehooks-ts"
import SearchField from "~/components/planning/SearchField"
import { FilterPill } from "~/components/planning/FilterPill"
import { PurchaseOrderFilterPopover } from "~/components/purchaseOrder/PurchaseOrderFilterPopover"
import { PO_BUY_PLAN_FILTER_FIELD, PO_FILTER_FIELDS } from "~/components/purchaseOrder/poFilters"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import { Button } from "~/components/design-system/components"
import { Module } from "~/components/design-system/pages"
import { type HeaderTab } from "~/components/reusable/HeaderTabs"
import {
  POTab,
  closedPOStatuses,
  draftTabPOStatuses,
  openPOStatuses,
} from "~/constants/purchaseOrders"
import { useGetPurchaseOrders } from "~/hooks/usePurchaseOrders"
import { useFilters } from "~/hooks/useFilters"
import CreatePurchaseOrderOptionsDialog from "~/pages/procurement/purchase-orders/CreatePurchaseOrderOptionsDialog"
import { FilterScopeProvider } from "~/providers/FilterScopeProvider"
import { useUser } from "~/providers/userProvider"
import { api } from "~/utils/api"
import { filterOnSearchString } from "~/utils/search"
import { toStringArray } from "~/utils/helpers"

const searchSchema = z.object({
  // One-shot bootstrap transports (see draft.tsx) — seed the real Filters system with a
  // PO ID / Buy Plan filter on redirect, then get cleared so a refresh doesn't re-trigger them.
  poIds: z.preprocess(toStringArray, z.array(z.string()).optional()),
  purchasePlanId: z.number().optional(),
})

export const Route = createFileRoute("/_auth/procurement/purchase-orders/_layout")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
})

function RouteComponent() {
  return (
    <FilterScopeProvider scopeKey={Route.id}>
      <PurchaseOrdersLayoutContent />
    </FilterScopeProvider>
  )
}

function PurchaseOrdersLayoutContent() {
  const { permissions } = useUser()
  const [searchText, setSearchText] = useSessionStorage("po-search-text", "")
  const { filters, setFilters, clearAll } = useFilters()
  const { data: vendors } = api.vendor.getAll.useQuery()
  const { data: warehouses } = api.warehouse.get.useQuery()
  const { data: purchasePlans } = api.purchasePlan.getAll.useQuery()
  const { data: purchaseOrders } = useGetPurchaseOrders()
  const { data: archivedPurchaseOrders } = useGetPurchaseOrders(undefined, true)
  const allPurchaseOrdersForFilters = useMemo(
    () => [...(purchaseOrders ?? []), ...(archivedPurchaseOrders ?? [])],
    [purchaseOrders, archivedPurchaseOrders],
  )
  const vendorNames = useMemo(() => {
    const relevantIds = new Set(allPurchaseOrdersForFilters.map((po) => po.vendorId))
    return (vendors ?? []).filter((v) => relevantIds.has(v.vendorId)).map((v) => v.name)
  }, [vendors, allPurchaseOrdersForFilters])
  const warehouseNames = useMemo(() => {
    const relevantIds = new Set(
      allPurchaseOrdersForFilters.flatMap((po) => po.items.map((item) => item.warehouseId)),
    )
    return (warehouses ?? []).filter((w) => relevantIds.has(w.id)).map((w) => w.name)
  }, [warehouses, allPurchaseOrdersForFilters])
  // Buy plan names have no uniqueness constraint (e.g. two buy plans from different supply
  // plans can both be "Buy 2026-08-01"), so the filter matches by id under the hood. The label
  // is always just the plain name — customers don't know/care about buy plan ids, and picking
  // between two identically-named plans in the rare collision case is on them.
  const purchasePlanOptions = useMemo(() => {
    const relevantIds = new Set(
      allPurchaseOrdersForFilters
        .map((po) => po.purchasePlanId)
        .filter((id): id is number => id != null),
    )
    return (purchasePlans ?? [])
      .filter((p) => relevantIds.has(p.id))
      .map((p) => ({ value: String(p.id), label: p.name }))
  }, [purchasePlans, allPurchaseOrdersForFilters])
  const purchasePlanLabelById = useMemo(
    () => new Map(purchasePlanOptions.map((o) => [o.value, o.label])),
    [purchasePlanOptions],
  )
  const isStaff = permissions.isStaff
  const draftTabPOStatusesFiltered = isStaff
    ? draftTabPOStatuses
    : draftTabPOStatuses.filter((s) => s !== "pre_draft")

  const tabCounts = useMemo(() => {
    const filtered = searchText
      ? filterOnSearchString(purchaseOrders ?? [], searchText)
      : (purchaseOrders ?? [])
    const filteredArchived = searchText
      ? filterOnSearchString(archivedPurchaseOrders ?? [], searchText)
      : (archivedPurchaseOrders ?? [])
    return {
      draft: filtered.filter((po) => draftTabPOStatusesFiltered.includes(po.status)).length,
      open: filtered.filter((po) => openPOStatuses.includes(po.status)).length,
      closed: filtered.filter((po) => closedPOStatuses.includes(po.status)).length,
      archived: filteredArchived.length,
    }
  }, [purchaseOrders, archivedPurchaseOrders, searchText, draftTabPOStatusesFiltered])

  const tabs: HeaderTab[] = useMemo(
    () => [
      {
        label: POTab.draft,
        icon: <FolderOpen />,
        path: "/procurement/purchase-orders/draft",
        count: tabCounts.draft,
      },
      {
        label: POTab.open,
        icon: <Folder />,
        path: "/procurement/purchase-orders/open",
        count: tabCounts.open,
      },
      {
        label: POTab.closed,
        icon: <CheckCircle />,
        path: "/procurement/purchase-orders/closed",
        count: tabCounts.closed,
      },
      {
        label: POTab.archived,
        icon: <Archive />,
        path: "/procurement/purchase-orders/archived",
        count: tabCounts.archived,
      },
    ],
    [tabCounts],
  )

  return (
    <Module
      breadcrumb={<BreadcrumbWrapper />}
      tabs={{ items: tabs, path: "/procurement/purchase-orders" }}
    >
      <div className="flex flex-row items-center justify-end gap-2 border-b border-base px-4 py-2">
        <SearchField
          initialSearchText={searchText}
          onSearchTextChange={setSearchText}
          placeholder="Search purchase orders..."
          containerClassName="w-[280px]"
        />
        <PurchaseOrderFilterPopover
          vendorNames={vendorNames}
          warehouseNames={warehouseNames}
          purchasePlanOptions={purchasePlanOptions}
          shortcutEnabled
          registerSearchCommands
        />
        <CreatePurchaseOrderOptionsDialog />
      </div>
      {filters.length > 0 && (
        <div className="flex flex-row items-center justify-between gap-2 px-4 pt-2">
          <div className="flex flex-row flex-wrap items-center gap-2">
            {filters.map((f, i) => {
              const field = PO_FILTER_FIELDS.find((pf) => pf.value === f.field)
              if (!field) return null
              return (
                <PurchaseOrderFilterPopover
                  key={`${f.field}-${i}`}
                  editingFilter={f}
                  editingFilterIndex={i}
                  vendorNames={vendorNames}
                  warehouseNames={warehouseNames}
                  purchasePlanOptions={purchasePlanOptions}
                  customTrigger={
                    <FilterPill
                      size="xs"
                      variant="primarySoft"
                      icon={field.icon}
                      showRemove
                      onRemove={() => void setFilters(filters.filter((_, idx) => idx !== i))}
                    >
                      {field.name}:{" "}
                      {f.field === PO_BUY_PLAN_FILTER_FIELD
                        ? f.values.map((v) => purchasePlanLabelById.get(v) ?? v).join(", ")
                        : f.values.join(", ")}
                    </FilterPill>
                  }
                />
              )
            })}
          </div>
          <Button variant="transparent" size="sm" onClick={() => void clearAll()}>
            Clear filters
          </Button>
        </div>
      )}
      <Outlet />
    </Module>
  )
}
